import imageCompression from "browser-image-compression";

import { multipartUpload } from "@/lib/r2-upload";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

const COMPRESSIBLE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const ACCEPT_STRING = ALLOWED_IMAGE_TYPES.join(",");

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MIN_DIMENSION = 200;
const COMPRESSION_TARGET_MB = 1;
const MAX_DIMENSION = 2048;

export async function validateAndUploadImage(
  file: File,
  accessToken: string,
  onProgress?: (progress: number) => void,
  onStatusChange?: (status: "compressing" | "uploading") => void,
): Promise<string> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Only JPG, PNG, WebP, and GIF files are allowed");
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error("Image too large (max 20 MB)");
  }

  await validateDimensions(file);

  let fileToUpload: File = file;

  if (
    COMPRESSIBLE_TYPES.includes(file.type) &&
    file.size > COMPRESSION_TARGET_MB * 1024 * 1024
  ) {
    onStatusChange?.("compressing");
    const compressed = await imageCompression(file, {
      fileType: file.type,
      initialQuality: 0.8,
      maxSizeMB: COMPRESSION_TARGET_MB,
      maxWidthOrHeight: MAX_DIMENSION,
      useWebWorker: true,
    });
    fileToUpload = new File([compressed], file.name, { type: file.type });
  }

  onStatusChange?.("uploading");

  const { url } = await multipartUpload(fileToUpload, accessToken, (p) =>
    onProgress?.(Math.round((p.loaded / p.total) * 100)),
  );

  return url;
}

function validateDimensions(file: File): Promise<void> {
  const img = new globalThis.Image();
  const objectUrl = URL.createObjectURL(file);
  return new Promise<void>((resolve, reject) => {
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (
        img.naturalWidth < MIN_DIMENSION ||
        img.naturalHeight < MIN_DIMENSION
      ) {
        reject(
          new Error(
            `Image must be at least ${MIN_DIMENSION}×${MIN_DIMENSION} pixels`,
          ),
        );
      } else {
        resolve();
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read image"));
    };
    img.src = objectUrl;
  });
}
